import { useRef, useEffect, useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { InputText } from 'primereact/inputtext';
import { useNavigate, useParams } from 'react-router-dom';
import { InputTextarea } from 'primereact/inputtextarea';
import api from '../../services/api';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { LANG_OPTIONS, tagOptions } from '../../constants/constants';
import { Controller, useForm } from 'react-hook-form';
import MetaTagsLiveCodePreview from '../EntryPages/EntryPage/MetaTagsLiveCodePreview';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import PreviewBlog from './PreviewBlog';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import TranslationsManager from '../../components/TranslationsManager';
import ProseMirrorComponent from '../../components/AdvancedProseMirrorEditor';
import {
  BLOG_TEMPLATE_OPTIONS,
  HEADING_LEVEL_OPTIONS,
  SECTION_CATALOG,
  clampHeadingLevel,
  createSection,
  normalizeSectionsForEditor,
  serializeSectionsForSave,
} from './blogSectionCatalog';
import {
  BLOG_CATEGORIES,
  getBlogCategoryIconSrc,
  getBlogCategoryLabel,
  resolveBlogCategory,
  toSavedBlogCategory,
} from './blogCategories';
import {
  PLACE_DAY_KEYS,
  PLACE_DAY_LABELS,
  PLACE_TIMEZONE_OPTIONS,
  normalizeHoursSchedule,
} from './placeHours';
import {
  BLOG_SEO_PACK_OPTIONS,
  buildBlogSeoPackTags,
  mergeBlogSeoPackTags,
} from './blogSeoPack';

const GUIDE_ITEM_TYPE_OPTIONS = [
  { label: 'Restaurant', value: 'Restaurant' },
  { label: 'LocalBusiness', value: 'LocalBusiness' },
];

const categoryOptionTemplate = (option) => {
  if (!option) return null;
  const src = getBlogCategoryIconSrc(option.icon || option.code);
  const label = getBlogCategoryLabel(option, 'ES');
  return (
    <div className="flex align-items-center gap-2">
      {src && <img src={src} alt="" width={18} height={18} style={{ display: 'block' }} />}
      <span>{label}</span>
    </div>
  );
};

const categoryValueTemplate = (option, props) => {
  if (!option) return props?.placeholder || 'Select category';
  return categoryOptionTemplate(option);
};

function BlogsManager() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [metaTags, setMetaTags] = useState([{ attributes: [{ key: '', value: '' }] }]);
  const [selectedSectionType, setSelectedSectionType] = useState(null);
  const [revalidationLoading, setRevalidationLoading] = useState(false);
  const [isRevalidateLoading, setIsRevalidateLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [seoPackDialogVisible, setSeoPackDialogVisible] = useState(false);
  const [seoPackSelected, setSeoPackSelected] = useState([]);
  const [seoPackGenerating, setSeoPackGenerating] = useState(false);
  const [seoPanelCollapsed, setSeoPanelCollapsed] = useState(true);
  const [contentPanelCollapsed, setContentPanelCollapsed] = useState(true);
  const [faqsPanelCollapsed, setFaqsPanelCollapsed] = useState(true);
  const [translationsPanelCollapsed, setTranslationsPanelCollapsed] = useState(true);
  /** UI-only default schema type for Template 2 list items (not persisted). */
  const [guideItemType, setGuideItemType] = useState('Restaurant');
  /** Expanded place_block section ids in the Guía / Lista editor. */
  const [expandedPlaceIds, setExpandedPlaceIds] = useState([]);

  const public_url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      _id: '',
      lang: '',
      template: 'template_1',
      slug: '',
      full_slug: '',
      title: '',
      hero: {
        breadcrumb: '',
        category: { code: '', label: '', icon: '' },
        title: '',
        description: '',
        image: { url: '', alt: '', title: '', caption: '' },
      },
      image: { url: '', alt: '', title: '', caption: '' },
      sections: [],
      faqs_section: { h3: '', p: '', items: [] },
      translations: [],
      related_posts: [],
      revalidated: false,
      revalidated_at: '',
      auto_revalidate: false,
      show_in_home: false,
      most_read: false,
    },
  });

  const watchLang = watch('lang');
  const watchSlug = watch('slug');
  const watchFullSlug = watch('full_slug');
  const watchTemplate = watch('template');
  const watchedSectionsRaw = watch('sections');
  const watchSections = useMemo(
    () => watchedSectionsRaw || [],
    [watchedSectionsRaw]
  );
  const watchTranslations = watch('translations');
  const watchFaqsItems = watch('faqs_section.items') || [];
  const watchHero = watch('hero');
  const watchRevalidated = watch('revalidated');
  const watchId = watch('_id');

  const sectionOptions = useMemo(() => {
    const raw = watchTemplate;
    const key =
      typeof raw === 'string'
        ? raw
        : raw?.code || raw?.value || 'template_1';
    return SECTION_CATALOG[key] || SECTION_CATALOG.template_1;
  }, [watchTemplate]);

  const resolvedTemplate = useMemo(() => {
    const raw = watchTemplate;
    if (typeof raw === 'string' && raw) return raw;
    return raw?.code || raw?.value || 'template_1';
  }, [watchTemplate]);

  const isTemplate2 = resolvedTemplate === 'template_2';

  const getPlaceRunBounds = (sections, index) => {
    if (!sections?.[index] || sections[index].type !== 'place_block') return null;
    let start = index;
    while (start > 0 && sections[start - 1]?.type === 'place_block') start -= 1;
    let end = index + 1;
    while (end < sections.length && sections[end]?.type === 'place_block') end += 1;
    return { start, end };
  };

  /** Document-order chunks: free sections + consecutive place_block runs as Guía clusters. */
  const sectionTimeline = useMemo(() => {
    const sections = watchSections || [];
    const chunks = [];
    let i = 0;
    while (i < sections.length) {
      if (sections[i]?.type === 'place_block') {
        let end = i + 1;
        while (end < sections.length && sections[end]?.type === 'place_block') end += 1;
        const start = i;
        chunks.push({
          kind: 'guide',
          start,
          end,
          entries: sections.slice(start, end).map((section, offset) => ({
            section,
            index: start + offset,
          })),
        });
        i = end;
      } else {
        chunks.push({ kind: 'section', index: i, section: sections[i] });
        i += 1;
      }
    }
    if (!chunks.some((c) => c.kind === 'guide')) {
      chunks.push({ kind: 'guide', start: sections.length, end: sections.length, entries: [] });
    }
    return chunks;
  }, [watchSections]);

  const editorialSectionOptions = useMemo(
    () => (sectionOptions || []).filter((opt) => opt.code !== 'place_block'),
    [sectionOptions]
  );

  const placeIdsKey = useMemo(
    () =>
      (watchSections || [])
        .filter((s) => s?.type === 'place_block')
        .map((s) => s?.id)
        .filter(Boolean)
        .join(','),
    [watchSections]
  );

  useEffect(() => {
    if (!isTemplate2 || !placeIdsKey) return;
    const ids = placeIdsKey.split(',');
    setExpandedPlaceIds((prev) => {
      const valid = prev.filter((id) => ids.includes(id));
      if (valid.length > 0) {
        if (valid.length === prev.length && valid.every((id, i) => id === prev[i])) {
          return prev;
        }
        return valid;
      }
      return ids[0] ? [ids[0]] : [];
    });
  }, [isTemplate2, placeIdsKey]);

  const convertToMetaTagsFormat = (tags) =>
    tags?.map((tag) => {
      const attributesArray = Object.entries(tag.attributes || {}).map(([key, value]) => ({
        key: key.trim(),
        value: value.toString().trim(),
      }));
      const typeTag = tagOptions.find((option) => option.code === tag.type) || {};
      return {
        type: typeTag || {},
        content: tag.content || '',
        name: tag.name || '',
        attributes: attributesArray,
      };
    });

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    api
      .get(`/blogs_pages/${id}`)
      .then((response) => {
        const data = response.data;
        const oldTags = convertToMetaTagsFormat(data?.tags || []);
        setMetaTags(oldTags?.length ? oldTags : [{ attributes: [{ key: '', value: '' }] }]);
        const selectedLang = LANG_OPTIONS.find((l) => l.code === data.lang) || '';
        const sections = normalizeSectionsForEditor(
          Array.isArray(data.sections)
            ? data.sections
            : Array.isArray(data.blog_sections)
              ? data.blog_sections
              : []
        );

        reset({
          _id: data._id,
          slug: data.slug ?? '',
          full_slug: data.full_slug ?? '',
          lang: selectedLang,
          template: data.template || 'template_1',
          title: data.title || data.hero?.title || '',
          hero: {
            breadcrumb: data.hero?.breadcrumb || '',
            category: {
              code: data.hero?.category?.code || '',
              label: data.hero?.category?.label || '',
              icon: data.hero?.category?.icon || data.hero?.category?.code || '',
            },
            title: data.hero?.title || data.title || '',
            description: data.hero?.description || '',
            image: data.hero?.image || data.image || { url: '', alt: '', title: '', caption: '' },
          },
          image: data.image || data.hero?.image || { url: '', alt: '', title: '', caption: '' },
          sections,
          faqs_section: {
            h3: data.faqs_section?.h3 || '',
            p: data.faqs_section?.p || '',
            items: (data.faqs_section?.items || []).map((faq) => ({
              question: faq.question || '',
              answer: faq.answer || '',
              answer_html: faq.answer_html || '',
              answer_html_updated: faq.answer_html || '',
            })),
          },
          translations: data.translations || [],
          related_posts: data.related_posts || [],
          revalidated: data.revalidated ?? false,
          revalidated_at: data.revalidated_at ?? '',
          auto_revalidate: data.auto_revalidate ?? false,
          show_in_home: Boolean(data.show_in_home),
          most_read: Boolean(data.most_read),
        });
      })
      .catch((error) => {
        console.error(error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load blog',
          life: 3000,
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const langSegment = (watchLang?.code || '').toLowerCase();
    const slugSegment = watchSlug?.trim() || '';
    let full_slug = `/${langSegment}/blog`;
    if (slugSegment) full_slug += `/${slugSegment}`;
    full_slug = full_slug.replace(/\/{2,}/g, '/');
    setValue('full_slug', full_slug);
  }, [watchLang, watchSlug, setValue]);

  const getFinalMetaTags = useMemo(
    () =>
      metaTags?.map((tag) => {
        const finalTag = {
          type: tag?.type?.code,
          name: tag.name || '',
          self_closing: tag?.type?.self_closing || false,
          content: tag?.content || '',
          attributes: {},
        };
        if (tag.attributes && Array.isArray(tag.attributes)) {
          tag.attributes.forEach((attr) => {
            if (attr.key && attr.key.trim() && attr.value !== undefined) {
              finalTag.attributes[attr.key.trim()] = attr.value.toString().trim();
            }
          });
        }
        return finalTag;
      }),
    [metaTags]
  );

  const addMetaTag = () => {
    const nextIndex = metaTags?.length || 0;
    const wasCollapsed = seoPanelCollapsed;
    setMetaTags([...(metaTags || []), { attributes: [{ key: '', value: '' }] }]);
    if (wasCollapsed) {
      setSeoPanelCollapsed(false);
    }
    setTimeout(() => {
      const el = document.getElementById(`seo-element-${nextIndex}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, wasCollapsed ? 180 : 80);
  };
  const removeMetaTag = (tagIndex) => {
    setMetaTags(metaTags.filter((_, i) => i !== tagIndex));
  };
  const addAttribute = (tagIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes.push({ key: '', value: '' });
    setMetaTags(updated);
  };
  const removeAttribute = (tagIndex, attrIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes = updated[tagIndex].attributes.filter((_, i) => i !== attrIndex);
    setMetaTags(updated);
  };
  const updateAttribute = (tagIndex, attrIndex, field, newValue) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes[attrIndex][field] = newValue;
    setMetaTags(updated);
  };
  const updateMainAttribute = (tagIndex, field, newValue) => {
    const updated = [...metaTags];
    updated[tagIndex][field] = newValue;
    setMetaTags(updated);
  };
  const updateTagType = (tagIndex, type) => {
    const updated = [...metaTags];
    updated[tagIndex].type = type;
    setMetaTags(updated);
  };

  const openSeoPackDialog = () => {
    setSeoPackSelected([]);
    setSeoPackDialogVisible(true);
  };

  const toggleSeoPackOption = (packId, checked) => {
    setSeoPackSelected((prev) => {
      if (checked) return prev.includes(packId) ? prev : [...prev, packId];
      return prev.filter((id) => id !== packId);
    });
  };

  const resolveTranslationSlugs = async (translations) => {
    try {
      const { data } = await api.get('/blogs_pages/translations');
      const byId = {};
      (Array.isArray(data) ? data : []).forEach((page) => {
        if (page?._id) byId[page._id] = page.full_slug || '';
      });
      return (translations || []).map((t) => ({
        ...t,
        full_slug: byId[t.blog_page_id] || t.full_slug || '',
      }));
    } catch {
      return translations || [];
    }
  };

  const handleGenerateSeoPack = async () => {
    if (!seoPackSelected.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'SEO pack',
        detail: 'Select at least one pack',
        life: 3000,
      });
      return;
    }

    setSeoPackGenerating(true);
    try {
      const hero = getValues('hero') || {};
      const translations = await resolveTranslationSlugs(getValues('translations') || []);
      const faqs = getValues('faqs_section.items') || [];
      const siteUrl = (public_url || 'https://bordergarita.com').replace(/\/$/, '');
      const heroImage =
        (hero.image?.url || getValues('image.url') || '').trim();

      const incoming = buildBlogSeoPackTags(
        {
          title: hero.title || getValues('title') || '',
          description: hero.description || '',
          image: heroImage,
          full_slug: getValues('full_slug') || '',
          lang: getValues('lang'),
          translations,
          faqs,
          siteUrl,
          datePublished: '',
          dateModified: getValues('revalidated_at') || '',
        },
        seoPackSelected
      ).map((tag) => {
        const matchedType = tagOptions.find((o) => o.code === tag.type?.code);
        return matchedType ? { ...tag, type: matchedType } : tag;
      });

      const { tags, added, skipped } = mergeBlogSeoPackTags(metaTags, incoming);
      setMetaTags(tags.length ? tags : [{ attributes: [{ key: '', value: '' }] }]);
      setSeoPackDialogVisible(false);

      toast.current?.show({
        severity: added ? 'success' : 'info',
        summary: 'SEO pack',
        detail:
          skipped > 0
            ? `Added ${added} tag(s). Skipped ${skipped} (already present).`
            : `Added ${added} tag(s).`,
        life: 4000,
      });
    } finally {
      setSeoPackGenerating(false);
    }
  };

  const handleAddFaq = () => {
    const faqs = getValues('faqs_section.items') || [];
    const nextIndex = faqs.length;
    setValue('faqs_section.items', [
      ...faqs,
      { question: '', answer: '', answer_html: '', answer_html_updated: '' },
    ]);
    trigger('faqs_section.items');
    setTimeout(() => {
      const input = document.getElementById(`faq-question-${nextIndex}`);
      if (!input) return;
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus();
    }, 50);
  };
  const handleRemoveFaq = (idx) => {
    const faqs = getValues('faqs_section.items') || [];
    setValue(
      'faqs_section.items',
      faqs.filter((_, i) => i !== idx)
    );
    trigger('faqs_section.items');
  };
  const handleFaqChange = (idx, field, value) => {
    const faqs = [...(getValues('faqs_section.items') || [])];
    faqs[idx] = { ...faqs[idx], [field]: value };
    setValue('faqs_section.items', faqs);
    trigger('faqs_section.items');
  };

  const handleAddTranslation = () => {
    const current = getValues('translations') || [];
    setValue('translations', [...current, { lang: '', blog_page_id: '' }]);
    trigger('translations');
  };
  const handleRemoveTranslation = (index) => {
    const current = getValues('translations') || [];
    setValue(
      'translations',
      current.filter((_, i) => i !== index)
    );
    trigger('translations');
  };

  const addSection = () => {
    if (!selectedSectionType) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Select a section type',
        life: 3000,
      });
      return;
    }
    const current = getValues('sections') || [];
    const created = createSection(selectedSectionType);
    setValue('sections', [...current, created]);
    setSelectedSectionType(null);
    setTimeout(() => {
      const el = document.getElementById(`blog-section-${created.id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const addPlaceBlock = (insertAt) => {
    const current = [...(getValues('sections') || [])];
    const created = createSection('place_block');
    if (typeof insertAt === 'number' && insertAt >= 0 && insertAt <= current.length) {
      current.splice(insertAt, 0, created);
    } else {
      let lastPlace = -1;
      for (let i = 0; i < current.length; i += 1) {
        if (current[i]?.type === 'place_block') lastPlace = i;
      }
      if (lastPlace >= 0) current.splice(lastPlace + 1, 0, created);
      else current.push(created);
    }
    setValue('sections', current);
    if (created.id) {
      setExpandedPlaceIds((prev) => [...prev, created.id]);
    }
  };

  const movePlaceBlock = (index, dir) => {
    const current = [...(getValues('sections') || [])];
    const bounds = getPlaceRunBounds(current, index);
    if (!bounds) return;
    const next = index + dir;
    if (next < bounds.start || next >= bounds.end) return;
    const tmp = current[index];
    current[index] = current[next];
    current[next] = tmp;
    setValue('sections', current);
  };

  /** Move a free section; if neighbor is a place cluster, jump over the whole run. */
  const moveSection = (index, dir) => {
    const current = [...(getValues('sections') || [])];
    if (index < 0 || index >= current.length) return;

    if (current[index]?.type === 'place_block') {
      movePlaceBlock(index, dir);
      return;
    }

    if (dir < 0) {
      if (index === 0) return;
      const neighborIdx = index - 1;
      if (current[neighborIdx]?.type === 'place_block') {
        const bounds = getPlaceRunBounds(current, neighborIdx);
        const [item] = current.splice(index, 1);
        current.splice(bounds.start, 0, item);
      } else {
        const tmp = current[index];
        current[index] = current[neighborIdx];
        current[neighborIdx] = tmp;
      }
    } else {
      if (index >= current.length - 1) return;
      const neighborIdx = index + 1;
      if (current[neighborIdx]?.type === 'place_block') {
        const bounds = getPlaceRunBounds(current, neighborIdx);
        const [item] = current.splice(index, 1);
        current.splice(bounds.end - 1, 0, item);
      } else {
        const tmp = current[index];
        current[index] = current[neighborIdx];
        current[neighborIdx] = tmp;
      }
    }
    setValue('sections', current);
  };

  /** Move an entire consecutive place_block run past the adjacent free section. */
  const moveGuideCluster = (start, end, dir) => {
    const current = [...(getValues('sections') || [])];
    if (start < 0 || end > current.length || start >= end) return;
    const run = current.slice(start, end);
    if (dir < 0) {
      if (start === 0) return;
      const before = current[start - 1];
      if (before?.type === 'place_block') return;
      current.splice(start, end - start);
      current.splice(start - 1, 0, ...run);
    } else {
      if (end >= current.length) return;
      const after = current[end];
      if (after?.type === 'place_block') return;
      current.splice(start, end - start);
      current.splice(start + 1, 0, ...run);
    }
    setValue('sections', current);
  };

  const togglePlaceExpanded = (sectionId) => {
    if (!sectionId) return;
    setExpandedPlaceIds((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    );
  };

  const removeSection = (index) => {
    const current = [...(getValues('sections') || [])];
    const removed = current[index];
    current.splice(index, 1);
    setValue('sections', current);
    if (removed?.id) {
      setExpandedPlaceIds((prev) => prev.filter((id) => id !== removed.id));
    }
  };

  const updateSectionData = (index, key, value) => {
    const current = [...(getValues('sections') || [])];
    current[index] = {
      ...current[index],
      data: { ...current[index].data, [key]: value },
    };
    setValue('sections', current);
  };

  const updatePlaceLink = (index, linkKey, field, value) => {
    const current = [...(getValues('sections') || [])];
    const data = { ...(current[index].data || {}) };
    const prevLink = data.links?.[linkKey];
    const normalized =
      prevLink && typeof prevLink === 'object'
        ? { url: prevLink.url || '', alt: prevLink.alt || '' }
        : { url: typeof prevLink === 'string' ? prevLink : '', alt: '' };
    data.links = {
      ...(data.links || {}),
      [linkKey]: { ...normalized, [field]: value },
    };
    current[index] = { ...current[index], data };
    setValue('sections', current);
  };

  const getPlaceLink = (links, key, legacyKey) => {
    const entry = links?.[key];
    if (entry && typeof entry === 'object') {
      return { url: entry.url || '', alt: entry.alt || '' };
    }
    if (typeof entry === 'string' && entry) {
      return { url: entry, alt: '' };
    }
    if (legacyKey && typeof links?.[legacyKey] === 'string') {
      return { url: links[legacyKey] || '', alt: '' };
    }
    return { url: '', alt: '' };
  };

  const updatePlaceHoursSchedule = (index, updater) => {
    const current = [...(getValues('sections') || [])];
    const data = { ...(current[index].data || {}) };
    const schedule = normalizeHoursSchedule(data.hours_schedule);
    data.hours_schedule = typeof updater === 'function' ? updater(schedule) : updater;
    current[index] = { ...current[index], data };
    setValue('sections', current);
  };

  const updatePlaceDayHours = (index, dayKey, field, value) => {
    updatePlaceHoursSchedule(index, (schedule) => ({
      ...schedule,
      days: {
        ...schedule.days,
        [dayKey]: {
          ...schedule.days[dayKey],
          [field]: value,
        },
      },
    }));
  };

  const openCloudinary = (onUrl) => {
    if (!window.cloudinary?.openUploadWidget) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cloudinary',
        detail: 'Upload widget not available',
        life: 3000,
      });
      return;
    }
    window.cloudinary.openUploadWidget(
      { cloudName: 'dtxar5b36', uploadPreset: 'bordergarita_uploads' },
      (error, result) => {
        if (!error && result.event === 'success') {
          onUrl(result.info.secure_url);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Image uploaded',
            life: 3000,
          });
        }
      }
    );
  };

  const isValidUrl = useMemo(() => {
    const langSegment = watchLang?.code;
    const slugSegment = watchSlug?.trim();
    return Boolean(langSegment && slugSegment);
  }, [watchLang, watchSlug]);

  const onSave = async (data) => {
    const templateCode =
      typeof data.template === 'string' && data.template
        ? data.template
        : data.template?.code || data.template?.value || 'template_1';
    const payload = {
      type: 'blog_page',
      template: templateCode,
      slug: data.slug,
      full_slug: data.full_slug,
      lang: data.lang?.code || data.lang || 'ES',
      title: data.hero?.title || data.title || '',
      hero: data.hero,
      image: data.hero?.image || data.image,
      sections: serializeSectionsForSave(data.sections || []),
      faqs_section: {
        h3: data.faqs_section?.h3 || '',
        p: data.faqs_section?.p || '',
        items: (data.faqs_section?.items || []).map((faq) => ({
          question: faq.question || '',
          answer: faq.answer || '',
          answer_html: faq.answer_html_updated || faq.answer_html || '',
        })),
      },
      translations: data.translations || [],
      related_posts: data.related_posts || [],
      tags: (getFinalMetaTags || []).filter((tag) => tag?.type),
      revalidated: data.revalidated,
      revalidated_at: data.revalidated_at,
      auto_revalidate: data.auto_revalidate,
      show_in_home: Boolean(data.show_in_home),
      most_read: Boolean(data.most_read),
    };

    try {
      setIsLoading(true);
      if (id) {
        await api.put(`/blogs_pages/${id}`, payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Blog updated',
          life: 3000,
        });
      } else {
        const response = await api.post('/blogs_pages', payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Blog created',
          life: 3000,
        });
        navigate(`/blogs/edit/${response.data._id}`);
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'Save failed',
        life: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFirstRevalidation = async () => {
    if (!id) return;
    setRevalidationLoading(true);
    try {
      const res = await api.put(`/blogs_pages/${id}`, {
        revalidated: true,
      });
      setValue('revalidated', true);
      trigger('revalidated');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Blog published successfully',
        life: 3000,
      });
      window.open(
        `${public_url}${res.data.full_slug || getValues('full_slug')}`,
        '_blank',
        'width=450,height=700'
      );
    } catch (e) {
      console.error(e);
    } finally {
      setRevalidationLoading(false);
    }
  };

  const onRevalidateUrl = async () => {
    const path = getValues('full_slug');
    const url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL;
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET;
    setIsRevalidateLoading(true);
    try {
      await fetch(`${url}/api/revalidate?secret=${secret}&path=${path}`, { method: 'POST' });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'URL revalidated',
        life: 3000,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsRevalidateLoading(false);
    }
  };

  const renderTocControls = (section, idx, { className = 'mb-3' } = {}) => {
    const data = section.data || {};
    const showInToc =
      typeof data.show_in_toc === 'boolean'
        ? data.show_in_toc
        : section.type === 'heading' || section.type === 'place_block';
    return (
      <div
        className={`${className} p-3 border-round surface-ground border-1 surface-border`.trim()}
        style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
      >
        <div>
          <strong>Block settings</strong>
          <div className="text-sm text-color-secondary mt-1">
            Table of contents visibility and URL fragment for this section.
          </div>
        </div>
        <div className="grid">
          <div className="col-12 md:col-5 flex align-items-center gap-3">
            <span className="flex-shrink-0" style={{ display: 'inline-flex', width: 'auto' }}>
              <InputSwitch
                inputId={`show-in-toc-${idx}`}
                checked={showInToc}
                onChange={(e) => updateSectionData(idx, 'show_in_toc', e.value)}
                style={{ width: '3rem' }}
              />
            </span>
            <label
              htmlFor={`show-in-toc-${idx}`}
              className="mb-0"
              style={{ lineHeight: 1.3, cursor: 'pointer' }}
            >
              <strong>Show in table of contents</strong>
            </label>
          </div>
          <div className="col-12 md:col-7">
            <label htmlFor={`section-anchor-${idx}`}>
              <strong>URL Fragment (HTML Id)</strong>
            </label>
            <InputText
              id={`section-anchor-${idx}`}
              value={data.anchor || ''}
              disabled={!showInToc}
              onChange={(e) => updateSectionData(idx, 'anchor', e.target.value)}
              className="w-full"
              placeholder="otros-lugares"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSectionEditor = (section, idx) => {
    const data = section.data || {};
    switch (section.type) {
      case 'heading':
        return (
          <div className="grid">
            <div className="col-3">
              <label>
                <strong>Level</strong>
              </label>
              <Dropdown
                value={clampHeadingLevel(data.level)}
                options={HEADING_LEVEL_OPTIONS}
                onChange={(e) => updateSectionData(idx, 'level', e.value)}
                className="w-full"
              />
            </div>
            <div className="col-9">
              <label>
                <strong>Text</strong>
              </label>
              <InputText
                value={data.text || ''}
                onChange={(e) => updateSectionData(idx, 'text', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12">
              <label>
                <strong>Rich text paragraph</strong>
              </label>
              <ProseMirrorComponent
                content={data.html || ''}
                onChange={(htmlContent) => updateSectionData(idx, 'html_updated', htmlContent)}
              />
            </div>
          </div>
        );
      case 'paragraph':
        return (
          <div>
            <label>
              <strong>Paragraph (rich text)</strong>
            </label>
            <ProseMirrorComponent
              content={data.html || ''}
              onChange={(htmlContent) => updateSectionData(idx, 'html_updated', htmlContent)}
            />
          </div>
        );
      case 'rich_text':
        return (
          <div>
            <label>
              <strong>HTML / text</strong>
            </label>
            <InputTextarea
              value={data.html || ''}
              onChange={(e) => updateSectionData(idx, 'html', e.target.value)}
              rows={6}
              className="w-full"
            />
          </div>
        );
      case 'quote':
        return (
          <div>
            <label>
              <strong>Quote</strong>
            </label>
            <InputTextarea
              value={data.text || ''}
              onChange={(e) => updateSectionData(idx, 'text', e.target.value)}
              rows={3}
              className="w-full"
            />
          </div>
        );
      case 'image':
        return (
          <div className="grid">
            <div className="col-12">
              <label>
                <strong>Image URL</strong>
              </label>
              <InputText
                value={data.url || ''}
                onChange={(e) => updateSectionData(idx, 'url', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12">
              <Button
                type="button"
                label="Upload"
                icon="pi pi-cloud-upload"
                onClick={() => openCloudinary((url) => updateSectionData(idx, 'url', url))}
              />
            </div>
            <div className="col-6">
              <label>
                <strong>Alt</strong>
              </label>
              <InputText
                value={data.alt || ''}
                onChange={(e) => updateSectionData(idx, 'alt', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-6">
              <label>
                <strong>Caption</strong>
              </label>
              <InputText
                value={data.caption || ''}
                onChange={(e) => updateSectionData(idx, 'caption', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-6">
              <label>
                <strong>Credit</strong>
              </label>
              <InputText
                value={data.credit || ''}
                onChange={(e) => updateSectionData(idx, 'credit', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-6 flex align-items-center gap-2 mt-4">
              <Checkbox
                inputId={`pinned-${idx}`}
                checked={Boolean(data.pinned)}
                onChange={(e) => updateSectionData(idx, 'pinned', e.checked)}
              />
              <label htmlFor={`pinned-${idx}`}>Pinned (Template 2)</label>
            </div>
            {data.url && (
              <div className="col-12">
                <img src={data.url} alt={data.alt || ''} style={{ maxWidth: '100%', maxHeight: 240 }} />
              </div>
            )}
          </div>
        );
      case 'place_block':
        return (
          <div className="grid">
            <div className="col-12">
              {renderTocControls(section, idx, { className: '' })}
            </div>

            <div className="col-12">
              <div className="text-xs font-semibold text-color-secondary mb-2 letter-spacing-1">
                IDENTIDAD
              </div>
              <div
                className="p-3 border-round surface-ground border-1 surface-border"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
              >
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>Nombre</strong>
                    </label>
                    <InputText
                      value={data.title || ''}
                      onChange={(e) => updateSectionData(idx, 'title', e.target.value)}
                      className="w-full"
                      placeholder="Nombre del lugar"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>Cocina</strong>{' '}
                      <span className="text-color-secondary font-normal">(servesCuisine)</span>
                    </label>
                    <InputText
                      value={data.specialty || ''}
                      onChange={(e) => updateSectionData(idx, 'specialty', e.target.value)}
                      className="w-full"
                      placeholder="Mexicana"
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Descripción / Antes de ir</strong>
                    </label>
                    <InputTextarea
                      value={data.before_you_go || ''}
                      onChange={(e) => updateSectionData(idx, 'before_you_go', e.target.value)}
                      rows={2}
                      className="w-full"
                      placeholder="Breve descripción / antes de ir"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>URL del lugar</strong>
                    </label>
                    <InputText
                      value={getPlaceLink(data.links, 'website', 'website_url').url}
                      onChange={(e) => updatePlaceLink(idx, 'website', 'url', e.target.value)}
                      className="w-full"
                      placeholder="/tijuana/tacos-el-franc"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>Chips</strong>{' '}
                      <span className="text-color-secondary font-normal">
                        (precio, etiquetas — ej. Taquería, $$)
                      </span>
                    </label>
                    <InputText
                      value={(data.chips || []).join(', ')}
                      onChange={(e) => {
                        const parts = e.target.value.split(',');
                        const chips = parts.map((part, i) =>
                          i === parts.length - 1
                            ? part.replace(/^\s+/, '')
                            : part.trim()
                        );
                        updateSectionData(idx, 'chips', chips);
                      }}
                      onBlur={() => {
                        const cleaned = (data.chips || [])
                          .map((c) => String(c).trim())
                          .filter(Boolean);
                        updateSectionData(idx, 'chips', cleaned);
                      }}
                      className="w-full"
                      placeholder="Taquería, $$, Outdoor seating"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>Desde la garita</strong>
                    </label>
                    <InputText
                      value={data.from_garita || ''}
                      onChange={(e) => updateSectionData(idx, 'from_garita', e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="text-xs font-semibold text-color-secondary mb-2">
                DIRECCIÓN
              </div>
              <div
                className="p-3 border-round surface-ground border-1 surface-border"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
              >
                <div>
                  <label>
                    <strong>Dirección</strong>
                  </label>
                  <InputTextarea
                    value={data.address || ''}
                    onChange={(e) => updateSectionData(idx, 'address', e.target.value)}
                    rows={2}
                    className="w-full"
                    placeholder="Calle, ciudad, CP…"
                  />
                </div>
              </div>
            </div>

            <div className="col-12">
              {(() => {
                const schedule = normalizeHoursSchedule(data.hours_schedule);
                return (
                  <div
                    className="p-3 border-round border-1 surface-border surface-card"
                    style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                  >
                    <div>
                      <strong>Horario por día</strong>
                      <div className="text-sm text-color-secondary mt-1">
                        El blog calcula Abierto/Cerrado en vivo según este horario y la zona
                        horaria.
                      </div>
                    </div>
                    <div className="grid">
                      <div className="col-12 md:col-6">
                        <label className="text-sm">
                          <strong>Timezone</strong>
                        </label>
                        <Dropdown
                          value={schedule.timezone}
                          options={PLACE_TIMEZONE_OPTIONS}
                          optionLabel="name"
                          optionValue="code"
                          onChange={(e) =>
                            updatePlaceHoursSchedule(idx, {
                              ...schedule,
                              timezone: e.value,
                            })
                          }
                          className="w-full"
                        />
                      </div>
                      <div className="col-12 md:col-6">
                        <label className="text-sm">
                          <strong>Nota de horario</strong> (opcional)
                        </label>
                        <InputText
                          value={data.hours || ''}
                          onChange={(e) =>
                            updateSectionData(idx, 'hours', e.target.value)
                          }
                          className="w-full"
                          placeholder="Ej. Último pedido 30 min antes"
                        />
                      </div>
                    </div>
                    {PLACE_DAY_KEYS.map((dayKey) => {
                      const day = schedule.days[dayKey] || {
                        closed: false,
                        open: '',
                        close: '',
                      };
                      return (
                        <div
                          key={dayKey}
                          className="p-3 border-round border-1 surface-border surface-card"
                        >
                          <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                            <strong>{PLACE_DAY_LABELS[dayKey].ES}</strong>
                            <div className="flex align-items-center gap-2">
                              <Checkbox
                                inputId={`closed-${idx}-${dayKey}`}
                                checked={Boolean(day.closed)}
                                onChange={(e) =>
                                  updatePlaceDayHours(
                                    idx,
                                    dayKey,
                                    'closed',
                                    e.checked
                                  )
                                }
                              />
                              <label htmlFor={`closed-${idx}-${dayKey}`}>Cerrado</label>
                            </div>
                          </div>
                          {!day.closed && (
                            <div className="grid">
                              <div className="col-6">
                                <label className="text-sm">Apertura</label>
                                <InputText
                                  type="time"
                                  value={day.open || ''}
                                  onChange={(e) =>
                                    updatePlaceDayHours(
                                      idx,
                                      dayKey,
                                      'open',
                                      e.target.value
                                    )
                                  }
                                  className="w-full"
                                />
                              </div>
                              <div className="col-6">
                                <label className="text-sm">Cierre</label>
                                <InputText
                                  type="time"
                                  value={day.close || ''}
                                  onChange={(e) =>
                                    updatePlaceDayHours(
                                      idx,
                                      dayKey,
                                      'close',
                                      e.target.value
                                    )
                                  }
                                  className="w-full"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="col-12">
              <label>
                <strong>Rich text paragraph</strong>
              </label>
              <div className="text-sm text-color-secondary mb-2">
                Shown below the place card on the public blog.
              </div>
              <ProseMirrorComponent
                content={data.html || ''}
                onChange={(htmlContent) => updateSectionData(idx, 'html_updated', htmlContent)}
              />
            </div>

            <div className="col-12">
              <div
                className="p-3 border-round surface-ground border-1 surface-border"
                style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
              >
                <div>
                  <strong>Links</strong>
                  <div className="text-sm text-color-secondary mt-1">
                    Cada enlace tiene URL y texto alternativo (alt / aria-label).
                  </div>
                </div>
                {[
                  { key: 'maps', label: 'Maps', legacy: 'maps_url' },
                  { key: 'website', label: 'Website', legacy: 'website_url' },
                  { key: 'facebook', label: 'Facebook' },
                  { key: 'instagram', label: 'Instagram' },
                  { key: 'tiktok', label: 'TikTok' },
                ].map((item) => {
                  const link = getPlaceLink(data.links, item.key, item.legacy);
                  return (
                    <div
                      key={item.key}
                      className="p-3 border-round border-1 surface-border surface-card"
                    >
                      <div className="mb-2 font-semibold flex align-items-center gap-2">
                        <img
                          src={`/place-${item.key}.svg`}
                          alt=""
                          width={16}
                          height={16}
                          aria-hidden
                        />
                        {item.label}
                      </div>
                      <div className="grid">
                        <div className="col-12 md:col-8">
                          <label className="text-sm">
                            <strong>URL</strong>
                          </label>
                          <InputText
                            value={link.url}
                            onChange={(e) =>
                              updatePlaceLink(idx, item.key, 'url', e.target.value)
                            }
                            className="w-full"
                            placeholder={`https://…`}
                          />
                        </div>
                        <div className="col-12 md:col-4">
                          <label className="text-sm">
                            <strong>Alt</strong>
                          </label>
                          <InputText
                            value={link.alt}
                            onChange={(e) =>
                              updatePlaceLink(idx, item.key, 'alt', e.target.value)
                            }
                            className="w-full"
                            placeholder={item.label}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      default:
        return <Message severity="warn" text={`Unknown section type: ${section.type}`} />;
    }
  };

  const showContentPanels = Boolean(watchId && watchRevalidated);

  return (
    <div id="blog-editor" className="grid">
      <Toast ref={toast} />
      <form
        className="p-fluid col-12"
        style={{ marginBottom: '5rem' }}
        onSubmit={handleSubmit(onSave)}
      >
        <div>
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-sm"
            type="button"
            onClick={() => navigate('/blogs')}
            aria-label="Go Back"
          />
        </div>

        <Card
          title={
            <div className="w-full">
              <strong>{watchId ? 'Edit blog' : 'New Blog'}</strong>
              <span className="ml-2 text-sm font-normal">{watchFullSlug}</span>
            </div>
          }
        >
          <Panel header="Configure blog page">
            <div className="grid">
              <div className="col-12 md:col-4">
                <label>
                  <strong>Language</strong>
                </label>
                <Controller
                  name="lang"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={LANG_OPTIONS}
                      optionDisabled="disabled"
                      optionLabel="name"
                      placeholder="Select a language"
                      className="w-full"
                    />
                  )}
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Template</strong>
                </label>
                <Controller
                  name="template"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={
                        typeof field.value === 'string'
                          ? field.value
                          : field.value?.code || field.value?.value || null
                      }
                      options={BLOG_TEMPLATE_OPTIONS}
                      optionLabel="name"
                      optionValue="code"
                      placeholder="Select template"
                      className="w-full"
                      disabled={Boolean(id) || Boolean(watchId)}
                      onChange={(e) => field.onChange(e.value)}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                    />
                  )}
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Path/Slug (URL)</strong>
                </label>
                <Controller
                  name="slug"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} className="w-full" placeholder="/blog-name-path" />
                  )}
                />
              </div>
              <div className="col-12">
                <label>
                  <strong>Full slug</strong> (auto)
                </label>
                <Controller
                  name="full_slug"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} className="w-full" disabled />
                  )}
                />
              </div>
            </div>

            {!watchId && !watchRevalidated && (
              <div className="grid mt-2">
                <div className="col-8">
                  <div>
                    {public_url}
                    {watchFullSlug}
                  </div>
                </div>
                <div className="col-4">
                  <Button
                    loading={isLoading}
                    label="Create blog"
                    disabled={!isValidUrl}
                    className="main-button"
                    type="submit"
                  />
                </div>
              </div>
            )}

            {watchId && !watchRevalidated && (
              <div className="grid mt-2">
                <div className="col-8">
                  <div className="mb-3">
                    {isValidUrl && (
                      <Message
                        severity="warn"
                        text="This link is not yet available for public access yet."
                        className="border-primary w-full justify-content-start"
                      />
                    )}
                  </div>
                  <div>
                    {public_url}
                    {watchFullSlug}
                  </div>
                  {revalidationLoading && (
                    <div className="mt-2">
                      <ProgressBar
                        mode="indeterminate"
                        style={{ height: '7px', '--value-color': '#0D0D65' }}
                      />
                    </div>
                  )}
                </div>
                <div className="col-4">
                  <Button
                    loading={revalidationLoading}
                    onClick={handleFirstRevalidation}
                    label="Publish Now"
                    tooltip="Triggers the first revalidation of this blog page's cache"
                    tooltipOptions={{ position: 'bottom' }}
                    severity="warning"
                    disabled={!isValidUrl}
                    className="p-button-primary w-full"
                    type="button"
                  />
                </div>
              </div>
            )}

            {watchId && watchRevalidated && (
              <>
                <div className="col-8">
                  <Message
                    severity="info"
                    text="This link is available for public access."
                    className="border-primary w-full justify-content-start"
                  />
                </div>
                <div className="col-12">
                  <a
                    href={`${public_url}${getValues('full_slug')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline hover:opacity-80 transition-opacity"
                  >
                    <div className="col-12 flex align-items-center">
                      <label className="text-md font-semibold mb-0 mr-2 cursor-pointer">
                        <strong>{`${public_url}${getValues('full_slug')}`}</strong>
                      </label>
                      <i className="pi pi-external-link" style={{ fontSize: '0.9rem' }} />
                    </div>
                  </a>
                </div>
              </>
            )}
          </Panel>
        </Card>

        {showContentPanels && (
          <>
            <Card className="mt-4">
              <Panel header="Manage basic info" className="mb-3">
                <div className="grid">
                  <div className="col-12">
                    <label>
                      <strong>Breadcrumb</strong>
                    </label>
                    <Controller
                      name="hero.breadcrumb"
                      control={control}
                      render={({ field }) => <InputText {...field} className="w-full" />}
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label>
                      <strong>Category</strong>
                    </label>
                    <Controller
                      name="hero.category"
                      control={control}
                      render={({ field }) => {
                        const selected = resolveBlogCategory(field.value);
                        const pageLang = watchLang?.code || watchLang || 'ES';
                        return (
                          <Dropdown
                            value={selected?.code ? selected : null}
                            options={BLOG_CATEGORIES}
                            optionLabel="label"
                            dataKey="code"
                            placeholder="Select category"
                            className="w-full"
                            showClear
                            itemTemplate={categoryOptionTemplate}
                            valueTemplate={categoryValueTemplate}
                            onChange={(e) => {
                              field.onChange(
                                e.value
                                  ? toSavedBlogCategory(e.value, pageLang)
                                  : { code: '', label: '', icon: '' }
                              );
                            }}
                          />
                        );
                      }}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Title</strong>
                    </label>
                    <Controller
                      name="hero.title"
                      control={control}
                      render={({ field }) => <InputText {...field} className="w-full" />}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Description</strong>
                    </label>
                    <Controller
                      name="hero.description"
                      control={control}
                      render={({ field }) => (
                        <InputTextarea {...field} rows={3} className="w-full" />
                      )}
                    />
                  </div>
                  <div className="col-12">
                    <div
                      className="p-3 border-round border-1 surface-border surface-card"
                      style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
                    >
                      <div className="flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                          <strong>Imagen del hero</strong>
                          <div className="text-sm text-color-secondary mt-1">
                            URL, texto alternativo y vista previa de la imagen principal.
                          </div>
                        </div>
                        <Button
                          type="button"
                          label="Upload"
                          icon="pi pi-cloud-upload"
                          className="w-auto"
                          onClick={() =>
                            openCloudinary((url) => {
                              setValue('hero.image.url', url);
                              setValue('image.url', url);
                            })
                          }
                        />
                      </div>
                      <div>
                        <label>
                          <strong>Hero image URL</strong>
                        </label>
                        <Controller
                          name="hero.image.url"
                          control={control}
                          render={({ field }) => <InputText {...field} className="w-full" />}
                        />
                      </div>
                      <div>
                        <label>
                          <strong>Image alt</strong>
                        </label>
                        <Controller
                          name="hero.image.alt"
                          control={control}
                          render={({ field }) => <InputText {...field} className="w-full" />}
                        />
                      </div>
                      {watchHero?.image?.url && (
                        <div>
                          <label>
                          <strong>Hero image preview</strong>
                          </label>
                          <img
                            src={watchHero.image.url}
                            alt={watchHero.image.alt || ''}
                            style={{
                              maxWidth: '100%',
                              maxHeight: 220,
                              borderRadius: 8,
                              display: 'block',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Panel>
            </Card>

            <Card
              className="mt-4"
              title="Content & SEO"
              subTitle="Translations, article body, meta tags and FAQs"
            >
              <Panel
                header="Manage Translations"
                className="mb-3"
                toggleable
                collapsed={translationsPanelCollapsed}
                onToggle={(e) => setTranslationsPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <TranslationsManager
                  translations={watchTranslations || []}
                  onAddTranslation={handleAddTranslation}
                  onRemoveTranslation={handleRemoveTranslation}
                  control={control}
                  currentLang={watchLang?.code || watchLang}
                  currentPageId={getValues('_id')}
                  idField="blog_page_id"
                  endpoint="/blogs_pages/translations"
                />
              </Panel>

              {isTemplate2 ? (
                <Panel
                  header="Manage Content"
                  className="mb-3"
                  toggleable
                  collapsed={contentPanelCollapsed}
                  onToggle={(e) => setContentPanelCollapsed(e.value)}
                  expandIcon="pi pi-chevron-down"
                  collapseIcon="pi pi-chevron-up"
                >
                  <div className="flex flex-wrap justify-content-end gap-2 align-items-end mb-3">
                    <div style={{ minWidth: '14rem', maxWidth: '20rem' }}>
                      <label>
                        <strong>Add section</strong>
                      </label>
                      <Dropdown
                        value={selectedSectionType}
                        options={editorialSectionOptions}
                        optionLabel="label"
                        optionValue="code"
                        placeholder="Select section type"
                        className="w-full"
                        onChange={(e) => setSelectedSectionType(e.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      label="Add section"
                      icon="pi pi-plus"
                      className="w-auto"
                      onClick={addSection}
                    />
                  </div>
                  {watchSections.length === 0 && (
                    <Message
                      severity="info"
                      text="No sections yet. Add a section above to build the article body."
                      className="mb-3 block"
                    />
                  )}
                  {sectionTimeline.map((chunk) => {
                    if (chunk.kind === 'section') {
                      const { section, index } = chunk;
                      return (
                        <div
                          key={section.id || `section-${index}`}
                          id={section.id ? `blog-section-${section.id}` : undefined}
                          className="p-3 mb-3 border-1 border-round surface-border"
                        >
                          <div className="flex justify-content-between align-items-center mb-3">
                            <strong>
                              {index + 1}. {section.type}
                            </strong>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                icon="pi pi-arrow-up"
                                className="p-button-text p-button-sm"
                                disabled={index === 0}
                                onClick={() => moveSection(index, -1)}
                              />
                              <Button
                                type="button"
                                icon="pi pi-arrow-down"
                                className="p-button-text p-button-sm"
                                disabled={index === watchSections.length - 1}
                                onClick={() => moveSection(index, 1)}
                              />
                              <Button
                                type="button"
                                icon="pi pi-trash"
                                className="p-button-danger p-button-sm"
                                onClick={() => removeSection(index)}
                              />
                            </div>
                          </div>
                          {renderTocControls(section, index)}
                          {renderSectionEditor(section, index)}
                        </div>
                      );
                    }

                    const placeCount = chunk.entries.length;
                    return (
                      <div
                        key={`guide-${chunk.start}-${chunk.end}`}
                        className="mb-3 border-1 border-round surface-border"
                      >
                        <div className="flex justify-content-between align-items-center flex-wrap gap-2 p-3 border-bottom-1 surface-border">
                          <strong>Guía / Lista</strong>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              icon="pi pi-arrow-up"
                              className="p-button-text p-button-sm"
                              disabled={chunk.start === 0 || placeCount === 0}
                              tooltip="Mover todo el listado arriba"
                              tooltipOptions={{ position: 'top' }}
                              onClick={() => moveGuideCluster(chunk.start, chunk.end, -1)}
                            />
                            <Button
                              type="button"
                              icon="pi pi-arrow-down"
                              className="p-button-text p-button-sm"
                              disabled={
                                placeCount === 0 || chunk.end >= watchSections.length
                              }
                              tooltip="Mover todo el listado abajo"
                              tooltipOptions={{ position: 'top' }}
                              onClick={() => moveGuideCluster(chunk.start, chunk.end, 1)}
                            />
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="grid">
                            <div className="col-12 lg:col-4">
                              <div
                                className="p-3 border-round border-1 surface-border surface-card h-full"
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '0.85rem',
                                }}
                              >
                                <div>
                                  <strong>Guide data</strong>
                                  <div className="text-sm text-color-secondary mt-1">
                                    Alimenta el hero y el listado de lugares (ItemList en una
                                    fase SEO posterior). Edita título y descripción en Manage
                                    Hero.
                                  </div>
                                </div>
                                <div>
                                  <label>
                                    <strong>Guide title</strong>
                                  </label>
                                  <Controller
                                    name="hero.title"
                                    control={control}
                                    render={({ field }) => (
                                      <InputTextarea
                                        {...field}
                                        rows={1}
                                        className="w-full"
                                        disabled
                                        autoResize
                                        placeholder="Dónde comer cerca de la garita…"
                                      />
                                    )}
                                  />
                                </div>
                                <div>
                                  <label>
                                    <strong>Guide description</strong>
                                  </label>
                                  <Controller
                                    name="hero.description"
                                    control={control}
                                    render={({ field }) => (
                                      <InputTextarea
                                        {...field}
                                        rows={3}
                                        className="w-full"
                                        disabled
                                        autoResize
                                        placeholder="Restaurantes y negocios a pasos del cruce."
                                      />
                                    )}
                                  />
                                </div>
                                <div>
                                  <label>
                                    <strong>Listed item type</strong>
                                  </label>
                                  <Dropdown
                                    value={guideItemType}
                                    options={GUIDE_ITEM_TYPE_OPTIONS}
                                    onChange={(e) => setGuideItemType(e.value)}
                                    className="w-full"
                                    disabled
                                  />
                                </div>
                                <div className="text-sm text-color-secondary">
                                  {placeCount}{' '}
                                  {placeCount === 1 ? 'item / place' : 'items / places'}
                                </div>
                              </div>
                            </div>

                            <div className="col-12 lg:col-8">
                              <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                                <div>
                                  <strong>Listed items</strong>
                                  <div className="text-sm text-color-secondary">
                                    Each item = a <code>place_block</code>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  label="Añadir lugar"
                                  icon="pi pi-plus"
                                  className="w-auto"
                                  onClick={() => addPlaceBlock(chunk.end)}
                                />
                              </div>

                              {placeCount === 0 && (
                                <Message
                                  severity="info"
                                  text="No hay lugares aún. Añade un place_block para armar el listado."
                                  className="mb-3 block"
                                />
                              )}

                              {chunk.entries.map(({ section, index }, placePos) => {
                                const isExpanded = expandedPlaceIds.includes(section.id);
                                const placeTitle =
                                  section.data?.title?.trim() || `Lugar ${placePos + 1}`;
                                return (
                                  <div
                                    key={section.id || index}
                                    className="mb-3 border-round surface-card"
                                    style={{
                                      border: '1px solid #b7e4c7',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <div
                                      className="flex justify-content-between align-items-center flex-wrap gap-2 p-3 cursor-pointer"
                                      style={{
                                        background: isExpanded ? '#f0faf4' : '#fff',
                                      }}
                                      onClick={() => togglePlaceExpanded(section.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          togglePlaceExpanded(section.id);
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <div className="flex align-items-center gap-2">
                                        <span
                                          className="flex align-items-center justify-content-center border-circle text-sm font-bold"
                                          style={{
                                            width: '1.75rem',
                                            height: '1.75rem',
                                            background: '#d8f3dc',
                                            color: '#1b4332',
                                          }}
                                        >
                                          {placePos + 1}
                                        </span>
                                        <strong>{placeTitle}</strong>
                                        <span
                                          className="text-xs font-semibold px-2 py-1 border-round"
                                          style={{
                                            background: '#d8f3dc',
                                            color: '#1b4332',
                                          }}
                                        >
                                          {guideItemType}
                                        </span>
                                      </div>
                                      <div
                                        className="flex gap-1 align-items-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Button
                                          type="button"
                                          icon="pi pi-arrow-up"
                                          className="p-button-text p-button-sm"
                                          disabled={placePos === 0}
                                          onClick={() => movePlaceBlock(index, -1)}
                                        />
                                        <Button
                                          type="button"
                                          icon="pi pi-arrow-down"
                                          className="p-button-text p-button-sm"
                                          disabled={placePos === placeCount - 1}
                                          onClick={() => movePlaceBlock(index, 1)}
                                        />
                                        <Button
                                          type="button"
                                          icon={
                                            isExpanded
                                              ? 'pi pi-chevron-up'
                                              : 'pi pi-chevron-down'
                                          }
                                          className="p-button-text p-button-sm"
                                          onClick={() => togglePlaceExpanded(section.id)}
                                        />
                                        <Button
                                          type="button"
                                          icon="pi pi-trash"
                                          className="p-button-danger p-button-sm"
                                          onClick={() => removeSection(index)}
                                        />
                                      </div>
                                    </div>
                                    {isExpanded ? (
                                      <div className="p-3 border-top-1 surface-border">
                                        {renderSectionEditor(section, index)}
                                      </div>
                                    ) : (
                                      <div className="px-3 pb-3 text-sm text-color-secondary">
                                        Mismo bloque de campos, colapsado. Cambia el tipo UI a
                                        LocalBusiness si el ítem no es restaurante.
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              ) : (
                <Panel
                  header="Manage Content"
                  className="mb-3"
                  toggleable
                  collapsed={contentPanelCollapsed}
                  onToggle={(e) => setContentPanelCollapsed(e.value)}
                  expandIcon="pi pi-chevron-down"
                  collapseIcon="pi pi-chevron-up"
                >
                  <div className="flex flex-wrap justify-content-end gap-2 align-items-end mb-3">
                    <div style={{ minWidth: '14rem', maxWidth: '20rem' }}>
                      <label>
                        <strong>Add section</strong>
                      </label>
                      <Dropdown
                        value={selectedSectionType}
                        options={sectionOptions}
                        optionLabel="label"
                        optionValue="code"
                        placeholder="Select section type"
                        className="w-full"
                        onChange={(e) => setSelectedSectionType(e.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      label="Add section"
                      icon="pi pi-plus"
                      className="w-auto"
                      onClick={addSection}
                    />
                  </div>
                  {watchSections.length === 0 && (
                    <Message
                      severity="info"
                      text="No sections yet. Add a section above to build the article body."
                      className="mb-3 block"
                    />
                  )}
                  {watchSections.map((section, idx) => (
                    <div
                      key={section.id || idx}
                      id={section.id ? `blog-section-${section.id}` : undefined}
                      className="p-3 mb-3 border-1 border-round surface-border"
                    >
                      <div className="flex justify-content-between align-items-center mb-3">
                        <strong>
                          {idx + 1}. {section.type}
                        </strong>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            icon="pi pi-arrow-up"
                            className="p-button-text p-button-sm"
                            disabled={idx === 0}
                            onClick={() => moveSection(idx, -1)}
                          />
                          <Button
                            type="button"
                            icon="pi pi-arrow-down"
                            className="p-button-text p-button-sm"
                            disabled={idx === watchSections.length - 1}
                            onClick={() => moveSection(idx, 1)}
                          />
                          <Button
                            type="button"
                            icon="pi pi-trash"
                            className="p-button-danger p-button-sm"
                            onClick={() => removeSection(idx)}
                          />
                        </div>
                      </div>
                      {section.type !== 'place_block' && renderTocControls(section, idx)}
                      {renderSectionEditor(section, idx)}
                    </div>
                  ))}
                </Panel>
              )}

              <Panel
                header="SEO & structured data"
                className="mb-3"
                toggleable
                collapsed={seoPanelCollapsed}
                onToggle={(e) => setSeoPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <div className="flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
                  <Button
                    type="button"
                    label="Add SEO element"
                    icon="pi pi-plus"
                    className="w-auto"
                    onClick={addMetaTag}
                  />
                  <Button
                    type="button"
                    label="Generate SEO batch"
                    className="p-button-outlined w-auto"
                    onClick={openSeoPackDialog}
                  />
                </div>
                {metaTags?.map((tag, tagIndex) => (
                  <div
                    key={tagIndex}
                    id={`seo-element-${tagIndex}`}
                    className="p-3 mb-3 border-round border-1 surface-border"
                  >
                    <div className="flex justify-content-between align-items-center mb-3">
                      <h4 className="m-0">
                        SEO element {tagIndex + 1}
                        {tag.name ? (
                          <span className="ml-2 text-sm font-normal text-color-secondary">
                            ({tag.name})
                          </span>
                        ) : null}
                      </h4>
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm"
                        type="button"
                        onClick={() => removeMetaTag(tagIndex)}
                      />
                    </div>
                    <label>
                      <strong>Type</strong>
                    </label>
                    <Dropdown
                      value={tag.type}
                      options={tagOptions}
                      filter
                      filterInputAutoFocus
                      filterBy="label"
                      optionLabel="label"
                      onChange={(e) => updateTagType(tagIndex, e.value)}
                      placeholder="Select a type"
                      className="w-full mb-2"
                    />
                    <label>
                      <strong>Content</strong>
                    </label>
                    <InputTextarea
                      value={tag.content || ''}
                      onChange={(e) => updateMainAttribute(tagIndex, 'content', e.target.value)}
                      rows={1}
                      autoResize
                      className="w-full mb-2"
                    />
                    {tag.attributes?.map((attr, attrIndex) => (
                      <div key={attrIndex} className="flex gap-2 mb-2">
                        <InputText
                          value={attr.key}
                          placeholder="key"
                          onChange={(e) =>
                            updateAttribute(tagIndex, attrIndex, 'key', e.target.value)
                          }
                          className="w-full"
                        />
                        <InputText
                          value={attr.value}
                          placeholder="value"
                          onChange={(e) =>
                            updateAttribute(tagIndex, attrIndex, 'value', e.target.value)
                          }
                          className="w-full"
                        />
                        <Button
                          icon="pi pi-times"
                          type="button"
                          className="p-button-text p-button-danger"
                          onClick={() => removeAttribute(tagIndex, attrIndex)}
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      label="Add Attribute"
                      icon="pi pi-plus"
                      className="p-button-text p-button-sm"
                      onClick={() => addAttribute(tagIndex)}
                    />
                  </div>
                ))}
                <div className="mt-3">
                  <MetaTagsLiveCodePreview metaTags={getFinalMetaTags} />
                </div>
              </Panel>

              <Panel
                header="Manage FAQs"
                toggleable
                collapsed={faqsPanelCollapsed}
                onToggle={(e) => setFaqsPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <div className="grid">
                  <div className="col-12">
                    <div className="col-12">
                      <label>
                        <strong>FAQ title (h3)</strong>
                      </label>
                      <Controller
                        name="faqs_section.h3"
                        control={control}
                        render={({ field }) => <InputText {...field} className="w-full" />}
                      />
                    </div>
                    <div className="col-12">
                      <label>
                        <strong>FAQ description</strong>
                      </label>
                      <Controller
                        name="faqs_section.p"
                        control={control}
                        render={({ field }) => (
                          <InputTextarea {...field} rows={2} className="w-full" />
                        )}
                      />
                    </div>
                    <div className="flex justify-content-end mb-3">
                      <Button
                        type="button"
                        label="Add FAQ"
                        icon="pi pi-plus"
                        className="w-auto"
                        onClick={handleAddFaq}
                      />
                    </div>
                    {watchFaqsItems.map((faq, idx) => (
                      <div
                        key={idx}
                        className="p-3 mb-2 border-1 border-round surface-border"
                        style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                      >
                        <div className="flex justify-content-between align-items-center mb-2">
                          <strong>Question {idx + 1}</strong>
                          <Button
                            icon="pi pi-trash"
                            type="button"
                            className="p-button-danger p-button-text"
                            onClick={() => handleRemoveFaq(idx)}
                            tooltip="Remove"
                          />
                        </div>
                        <InputText
                          id={`faq-question-${idx}`}
                          value={faq.question || ''}
                          placeholder="Question"
                          className="w-full mb-2"
                          onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                        />
                        <InputTextarea
                          value={faq.answer || ''}
                          placeholder="Answer"
                          rows={3}
                          className="w-full"
                          autoResize
                          onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                        />
                        <ProseMirrorComponent
                          content={faq.answer_html || ''}
                          onChange={(htmlContent) => {
                            handleFaqChange(idx, 'answer_html_updated', htmlContent);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            </Card>

            <Card
              className="mt-4"
              title="Blog settings"
              subTitle="Configure blog settings"
            >
              <Panel header="Configure visibility">
                <div className="grid">
                  <div className="col-12 flex align-items-center gap-2">
                    <Controller
                      name="show_in_home"
                      control={control}
                      render={({ field }) => (
                        <InputSwitch
                          inputId="show-in-home"
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                    <label htmlFor="show-in-home" className="mb-0">
                      <strong>Show in home</strong>
                      <span className="ml-2 text-sm font-normal text-color-secondary">
                        Visible in the Blogs section on /es or /en home
                      </span>
                    </label>
                  </div>
                  <div className="col-12 flex align-items-center gap-2">
                    <Controller
                      name="most_read"
                      control={control}
                      render={({ field }) => (
                        <InputSwitch
                          inputId="most-read"
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                    <label htmlFor="most-read" className="mb-0">
                      <strong>Most read</strong>
                      <span className="ml-2 text-sm font-normal text-color-secondary">
                        Visible in LO MÁS LEÍDO on /es/blog or /en/blog
                      </span>
                    </label>
                  </div>
                </div>
              </Panel>
            </Card>

            <Dialog
              header="Generate SEO batch"
              visible={seoPackDialogVisible}
              onHide={() => setSeoPackDialogVisible(false)}
              style={{ width: '32rem', maxWidth: '95vw' }}
              footer={
                <div className="flex justify-content-end gap-2">
                  <Button
                    type="button"
                    label="Cancel"
                    className="p-button-text"
                    onClick={() => setSeoPackDialogVisible(false)}
                    disabled={seoPackGenerating}
                  />
                  <Button
                    type="button"
                    label="Generate"
                    icon="pi pi-check"
                    onClick={handleGenerateSeoPack}
                    loading={seoPackGenerating}
                  />
                </div>
              }
            >
              <p className="mt-0 mb-3 text-sm text-color-secondary">
                Creates real meta/schema tags and merges them (skips packs already present). Blog
                fields are filled when available; empty values are left for you to complete.
              </p>
              <div className="flex flex-column gap-3">
                {BLOG_SEO_PACK_OPTIONS.map((pack) => (
                  <div key={pack.id} className="flex align-items-start gap-2">
                    <Checkbox
                      inputId={`seo-pack-${pack.id}`}
                      checked={seoPackSelected.includes(pack.id)}
                      onChange={(e) => toggleSeoPackOption(pack.id, e.checked)}
                    />
                    <label htmlFor={`seo-pack-${pack.id}`} className="cursor-pointer">
                      <div className="font-medium">{pack.label}</div>
                      <div className="text-sm text-color-secondary">{pack.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </Dialog>

            <Dialog
              header="Blog preview"
              visible={previewVisible}
              onHide={() => setPreviewVisible(false)}
              style={{ width: '95vw', maxWidth: 1280 }}
              breakpoints={{ '960px': '98vw' }}
              maximizable
              blockScroll
              dismissableMask
              contentStyle={{
                padding: 0,
                background: '#e5e7eb',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
            >
              <div style={{ padding: '24px 16px' }}>
                <PreviewBlog
                  sections={watchSections}
                  hero={watchHero}
                  template={resolvedTemplate}
                  faqs_section={getValues('faqs_section')}
                  full_slug={watchFullSlug}
                />
              </div>
            </Dialog>

            <div
              className="col-12"
              style={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '720px',
                maxWidth: '95vw',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: 'white',
                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                marginTop: '0',
              }}
            >
              <Button
                type="button"
                label="Publish"
                disabled={isRevalidateLoading}
                loading={isRevalidateLoading}
                onClick={onRevalidateUrl}
                icon="pi pi-globe"
                severity="warning"
                style={{ width: '200px' }}
              />

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button
                  type="button"
                  label="Preview"
                  icon="pi pi-eye"
                  className="p-button-outlined"
                  onClick={() => setPreviewVisible(true)}
                  style={{ width: '160px' }}
                />
                <Button
                  loading={isLoading}
                  label="Save"
                  icon="pi pi-save"
                  className="main-button"
                  type="submit"
                  style={{ width: '200px' }}
                />
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default BlogsManager;
